// Reading a switch's running configuration over its console (ZENITH.md §11.2,
// ADR-0115, #773), on the switch's Ports tab.
//
// `/v1`'s `running-configuration:read` accepts a read and answers `202` with its
// id at once; the read runs on the server whether or not this page is open, and
// its progress, then its document or what ended it, is its record's (ADR-0530,
// #2245). **It is a read and is offered where reads are** (ADR-0149 decision 2):
// on a device's page in Operate, and beside the port intent in Design. What
// turns the document into intent is the existing import (ADR-0014), a write, so
// it is Design's and is handed in through `after` (`RunningConfigImport.tsx`);
// this module makes no write and Operate's guard names the POSTs it does make. A
// stub-only switch's first document is sent from Design as well, in the
// switch's *Review & apply* sheet (`ReviewApply.tsx`, ADR-0215), which draws
// this read as its first step (#1891).
//
// Before a read the panel lists the `show` commands the read types for the
// switch's model and build, from `/v1`'s `running-configuration/commands`. The
// read's id is remembered for the switch in this browser, so a panel closed or
// reloaded mid-read finds the read again and follows it: the record is read
// when the change stream says a read moved (`kind: switch_read`), when the
// stream reconnects, and when the panel opens. Nothing is polled. The starter may
// stop it; closing the panel never does (decision 5a).
//
// **A switch's last read is its copy, and the panel opens on it** (ADR-0560,
// #2393). With no read remembered, the panel reads `read-copy`, which opens no
// session, and follows the copy's record. *Read the switch* answers the copy
// where there is one; *Read the switch again* asks `again`, and only that opens
// the console. The copy is dropped when someone opens the switch's terminal or
// Zenith writes to it, so the panel says when it was read and never calls it
// current.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { api, idempotencyKey, unattended } from "../../api/client";
import { oneConsole } from "../../api/oneConsole";
import { SKIP, useQuery } from "../../api/hooks";
import { useMutation } from "../../api/mutation";
import { describe } from "../../api/problem";
import type { components } from "../../api/schema";
import { Info, LearnMore, Pill, ProblemBox } from "../../ui";
import type { Device } from "./commands";
import { PortName } from "./DeviceFace";
import { corpusSummary, unchangedSentence } from "./firstDocumentWords";
import { inFirstOrder } from "../networks/groupLines";
import { listed, unrecognized, wordFor } from "../../api/openEnum";

export type Reading = components["schemas"]["SwitchRunningConfiguration"];
type SwitchRead = components["schemas"]["SwitchRead"];

/** Where this browser remembers a switch's last read, so a panel opened again
 * finds it (ADR-0530 decision 1a). A per-viewer convenience: storage that
 * throws or comes back empty costs only the reattachment. */
const readKey = (serial: string) => `zenith.switch-read.${serial}`;
export function rememberedRead(serial: string): string | undefined {
  try { return localStorage.getItem(readKey(serial)) || undefined; } catch { return undefined; }
}
function rememberRead(serial: string, id: string | undefined) {
  try {
    if (id) localStorage.setItem(readKey(serial), id);
    else localStorage.removeItem(readKey(serial));
  } catch { /* private mode */ }
}

/** m:ss, or h:mm:ss past an hour. */
function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor((s % 3600) / 60), ss = String(s % 60).padStart(2, "0");
  return s >= 3600 ? `${Math.floor(s / 3600)}:${String(mm).padStart(2, "0")}:${ss}` : `${mm}:${ss}`;
}

const STEP_WORDS: Record<components["schemas"]["SwitchReadStepState"] | "not_typed", string> = {
  typing: "typing",
  answered: "answered",
  not_answered: "not answered",
  not_typed: "not typed",
};
type Item = components["schemas"]["SwitchRunningConfigurationItem"];
// The named lists: since ADR-0511 each response field is its list or any other string.
type Disposition = components["schemas"]["SwitchRunningConfigurationItemDisposition"];

const DISPOSITION_WORDS: Record<Disposition, { label: string; tone: "good" | "info" | "warn" | "serious" }> = {
  read: { label: "read from the switch", tone: "good" },
  supplied_from_corpus: { label: "supplied from the corpus", tone: "info" },
  not_expressible: { label: "not expressible", tone: "warn" },
  refused: { label: "refused", tone: "serious" },
};

// Restates ADR-0215 decision 7e — a refusal a first document may proceed past is one of two kinds, and a
// native setting's fate on an apply is measured or it is not.
const REFUSED_KIND_WORDS: Record<components["schemas"]["SwitchRunningConfigurationItemRefusedKind"], string> = {
  omitted_by_rule: "left out on purpose",
  supplied_at_apply: "filled in when sent",
};
const FATE_WORDS: Record<components["schemas"]["SwitchRunningConfigurationItemFate"], string> = {
  unmeasured: "what applying a configuration does to it is not measured",
  kept: "measured to stay on the switch",
  removed: "measured to be taken off the switch",
};

/** The facts row's words for each class a first configuration puts an element in (#1940). */
const EFFECT_WORDS: Record<Effect, [one: string, many: string]> = {
  stops: ["stops the first configuration", "stop the first configuration"],
  asks: ["asks for an acceptance", "ask for an acceptance"],
  none: ["needs nothing", "need nothing"],
};

/** The port a comparison element names, or the element itself. */
const portOf = (element: string) => element.match(/^ethernet\[(.+)\]$/)?.[1];

/** The headline over a read whose candidate this service's own gate refused
 * (#1788). The refusal is `conflict` since ADR-0393 decision 2a, whose banner
 * would otherwise head with the server's sentence; this one says it in the
 * operator's words and is true of this refusal alone. */
export const CANDIDATE_REFUSED = "This service’s own check refused the candidate first configuration. Nothing was sent to the controller.";

type Effect = components["schemas"]["SwitchRunningConfigurationItemFirstDocumentEffect"];

type SwitchSessionHeld = components["schemas"]["SwitchSessionHeld"];

/** A console's idle bound in words: whole minutes as minutes, else seconds. */
const idleWords = (seconds: number) =>
  seconds % 60 === 0 ? (seconds === 60 ? "1 minute" : `${seconds / 60} minutes`) : `${seconds} seconds`;

/** What holds the switch when a read is refused, and when it lets go (#2241).
 * `ends_by` is the holding session's own bound, so the switch is free by then
 * at the latest. An attached console has none (#2340): it lasts until it is
 * closed, and closes once nothing has moved on it for `idle_seconds`, so no
 * time is shown for it and *Read again* is offered at once. A read this page
 * asked for is joined rather than refused (ADR-0530 decision 3a), so a read
 * that holds the switch here is another act's own, such as an apply's. It
 * names no person: `yours` says only whether it is the caller's. */
export function HeldSwitch({ held, onRead, now: fixedNow }: { held: SwitchSessionHeld; onRead: () => void; now?: number }) {
  const ends = held.ends_by === undefined ? undefined : Date.parse(held.ends_by);
  const [now, setNow] = useState(() => fixedNow ?? Date.now());
  useEffect(() => {
    if (fixedNow !== undefined || ends === undefined || now >= ends) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [fixedNow, now, ends]);
  const what = held.kind === "read"
    ? (held.yours ? "Your earlier read of this switch is still running." : "Another session is reading this switch.")
    : held.kind === "poe_cycle" ? "A PoE cycle is open on this switch." : "A console is open on this switch.";
  let when: ReactNode;
  let free: boolean;
  if (ends === undefined) {
    // Restates #2340 — an attached console has no fixed end: it lasts until it is closed, or until nothing moves on it for CONSOLE_IDLE_TIMEOUT.
    const idle = held.idle_seconds;
    when = idle === undefined
      ? "It lasts until that console is closed."
      : `It lasts until that console is closed, or until nothing has been typed or printed on it for ${idleWords(idle)}.`;
    free = true;
  } else {
    const at = new Date(ends).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    free = (fixedNow ?? now) >= ends;
    when = free
      ? "Its time is up, so the switch should be free now."
      : <>It ends by <time dateTime={held.ends_by}>{at}</time> at the latest.</>;
  }
  return (
    <div role="status" aria-label="Switch held">
      <p>
        {what} {when}
      </p>
      {free && (
        <div className="actions">
          <button type="button" className="btn" onClick={onRead}>Read again</button>
        </div>
      )}
    </div>
  );
}

/** What a first configuration does with an element, as `/v1` serves it from the
 * rule the first-document gate aggregates (#1940). Nothing here re-derives it
 * from the disposition, kind and fate; a value this build does not know is read
 * as the costly one, as the gate reads a disposition it does not know. */
const effectOf = (it: Item): Effect =>
  it.first_document_effect === "none" || it.first_document_effect === "asks" ? it.first_document_effect : "stops";

/** The port an element names, as `ethernet[<port>]` at its start. */
const PORT_IN_ELEMENT = /^ethernet\[([^\]]+)\]/;
/** A port as the switch's own lines name it, inside a reason: `Eth 1/17`. */
const PORT_IN_REASON = /\bEth(?:ernet)? ?\d+\/\d+(?:\/\d+)?\b/gi;

/** Rows identical except for their port, as one key: the element with its port
 * taken out, and the reason with the switch's own port name taken out. */
function sameExceptPort(it: Item): string {
  return [it.disposition, it.refused_kind ?? "", it.fate ?? "", effectOf(it),
    it.element.replace(PORT_IN_ELEMENT, "ethernet[]"), (it.reason ?? "").replace(PORT_IN_REASON, "")].join("\u0000");
}

/** One line of the list: an element, or every element that says the same thing
 * on a different port, counted, with every port named (#1753's form). */
function ElementLine({ items }: { items: Item[] }) {
  const it = items[0]!;
  const ports = items.length > 1 ? items.map((x) => PORT_IN_ELEMENT.exec(x.element)?.[1] ?? x.element) : [];
  const element = items.length > 1 ? it.element.replace(PORT_IN_ELEMENT, "ethernet[…]") : it.element;
  const reason = items.length > 1 ? it.reason?.replace(PORT_IN_REASON, "each port") : it.reason;
  return (
    <li>{listed(DISPOSITION_WORDS, it.disposition)
      ? <Pill tone={DISPOSITION_WORDS[it.disposition].tone}>{DISPOSITION_WORDS[it.disposition].label}</Pill>
      : <Pill tone="serious">{unrecognized(it.disposition)}</Pill>} <code>{element}</code>
      {ports.length > 0 && <> on {ports.length} ports: {ports.map((p, i) => <span key={i}>{i > 0 && ", "}<code>{p}</code></span>)}</>}
      {it.refused_kind && <> <span className="muted">({wordFor(REFUSED_KIND_WORDS, it.refused_kind)})</span></>}
      {it.fate && <> <span className="muted">({wordFor(FATE_WORDS, it.fate)})</span></>}
      {reason && <> — {reason}</>}</li>
  );
}

/** An element's lines, grouped only where the element names a port: any other
 * element is a line of its own, even where two say the same thing. */
function Lines({ items, label }: { items: readonly Item[]; label: string }) {
  const groups = inFirstOrder(items.map((it, i) => ({ it, i })), ({ it, i }) => (PORT_IN_ELEMENT.test(it.element) ? sameExceptPort(it) : `#${i}`));
  return <ul aria-label={label}>{groups.map((g) => <ElementLine key={g.key} items={g.items.map((x) => x.it)} />)}</ul>;
}

/** "1 element" or "N elements". */
const elements = (n: number) => `${n} ${n === 1 ? "element" : "elements"}`;

/** Every element of a read, with what became of it. Where the read is a candidate
 * first configuration (`lead`), what stops it being sent and what it asks to be
 * accepted are drawn first and open, and every element that needs nothing is
 * folded behind a disclosure, never left out (ADR-0215 decision 2c, ruled on
 * #1940). A switch whose controller holds a configuration gets no lead: nothing
 * there is a first configuration (ruling 2), so its elements are grouped and
 * nothing more. */
function Elements({ items, lead, first, model }: { items: Reading["items"]; lead: boolean; first?: Reading["first_document"]; model?: string }) {
  if (!lead) {
    return (
      <details>
        <summary>Every element, with what became of it</summary>
        <Lines items={items} label="Every element" />
      </details>
    );
  }
  const stops = items.filter((it) => effectOf(it) === "stops");
  const asks = items.filter((it) => effectOf(it) === "asks");
  const corpus = asks.filter((it) => it.disposition === "supplied_from_corpus");
  const losses = asks.filter((it) => it.disposition !== "supplied_from_corpus");
  const rest = items.filter((it) => effectOf(it) === "none");
  const summary = first ? corpusSummary(first, model) : undefined;
  return (
    <>
      {/* Restates ADR-0215 decision 7e, ADR-0233 and ADR-0538 decisions 1 and 4 — a first configuration is not sent while
          any element is refused for a reason other than the two it proceeds past, or a native setting's fate is
          unmeasured; the values from the corpus are one summary that applying accepts, with every element under Details;
          a setting the switch has on that the apply is measured to take off keeps its own line; and one whose effective
          value does not change is said in the summary and asks nothing. */}
      {stops.length > 0 && (
        <>
          <p><strong>What stops the first configuration being sent</strong> — none of these can be accepted past:</p>
          <Lines items={stops} label="What stops the first configuration" />
        </>
      )}
      {corpus.length > 0 && (
        <>
          <p data-corpus-summary=""><strong>Standard values</strong> — {summary ?? `${elements(corpus.length)} this switch doesn't report will be set from the corpus configurations for its model.`} Applying accepts them.</p>
          {(first?.unchanged ?? []).map((u) => <p key={u.element} className="muted">{unchangedSentence(u)}</p>)}
          <details>
            <summary>Details</summary>
            <Lines items={corpus} label="Values supplied from the corpus" />
          </details>
        </>
      )}
      {corpus.length === 0 && (first?.unchanged ?? []).map((u) => <p key={u.element} className="muted">{unchangedSentence(u)}</p>)}
      {losses.length > 0 && (
        <>
          <p><strong>What sending it takes off the switch</strong>, one acceptance each:</p>
          <Lines items={losses} label="What sending it asks you to accept" />
        </>
      )}
      {stops.length === 0 && asks.length === 0 && (
        <p>Nothing this read found stops a first configuration or asks for an acceptance.</p>
      )}
      {rest.length > 0 && (
        <details>
          <summary>{stops.length + asks.length > 0 ? `${rest.length} more ${rest.length === 1 ? "element needs" : "elements need"} nothing` : `${elements(rest.length)}, none of which needs anything`}</summary>
          <Lines items={rest} label="Elements that need nothing" />
        </details>
      )}
    </>
  );
}

export default function RunningConfigRead({ device, mayRead, after, embedded = false, step = false, onReading, onBusy, again = 0 }: {
  device: Device;
  /** `device.cli.read` at the switch's site (ADR-0155 decision 2b). */
  mayRead: boolean;
  /** What follows a reading, where the page offers it: Design's import. */
  after?: (reading: Reading) => ReactNode;
  /** Drawn inside a device's Configuration section in Operate (#1801), under
   * the held and running pair, rather than as a panel of its own. */
  embedded?: boolean;
  /** Drawn as the first step of the switch's *Review & apply* sheet (#1891):
   * no heading of its own, and the sentences that restate a ruling behind
   * *Learn more*. */
  step?: boolean;
  /** Told each reading as it arrives, and `undefined` when a new read starts. */
  /** `readId` is the read's record id where there is a reading, which a first document with planned ports is planned from (ADR-0555). */
  onReading?(reading: Reading | undefined, readId?: string): void;
  /** Told when a read starts and when it ends, however it ends. */
  onBusy?(busy: boolean): void;
  /** Reads again each time this grows: the sheet's redraw after a refused
   * first-document apply (ADR-0436 decision 3a (vi)). */
  again?: number;
}) {
  const [reading, setReading] = useState<Reading>();
  const readLane = useMutation();
  const stopLane = useMutation();
  // The read this panel follows, by id, and its record as last read (ADR-0530
  // decision 2b): its progress while it runs, then how it ended.
  const [readId, setReadId] = useState(() => rememberedRead(device.serial));
  const [record, setRecord] = useState<SwitchRead>();
  const [now, setNow] = useState(() => Date.now());
  const commands = useQuery(
    () => (mayRead ? api.GET("/switches/{serial}/running-configuration/commands", { params: { path: { serial: device.serial } } }) : SKIP),
    [device.serial, mayRead],
  );
  const running = record?.state === "running";
  const busy = readLane.busy || running;
  useEffect(() => {
    if (!running) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [running]);
  useEffect(() => { onBusy?.(busy); }, [busy, onBusy]);

  // The switch's copy, which opens no session (ADR-0560): the record the panel
  // follows when it remembers none, or when the one it remembers is gone.
  const openOnCopy = useCallback(async () => {
    const r = await api.GET("/switches/{serial}/read-copy", { params: { path: { serial: device.serial } } });
    if (r.data?.read) setReadId(r.data.read.id);
  }, [device.serial]);
  // The record, read once on open (a read remembered from before a close or a
  // reload) and then whenever the change stream says a read moved.
  const follow = useCallback(async (id: string, how?: { unattended: boolean }) => {
    const r = await api.GET("/switches/{serial}/reads/{read_id}", {
      params: { path: { serial: device.serial, read_id: id } }, headers: unattended(how),
    });
    if (r.data) { setRecord(r.data); return; }
    // Kept no longer, or never this caller's to read: nothing to reattach to,
    // so the panel opens on the switch's copy where it has one.
    if (r.response.status === 404) {
      rememberRead(device.serial, undefined); setReadId(undefined); setRecord(undefined);
      void openOnCopy();
    }
  }, [device.serial, openOnCopy]);
  useEffect(() => {
    if (!mayRead) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the record is read from the server; its state lands after the await
    if (readId) void follow(readId);
    else void openOnCopy();
  }, [mayRead, readId, follow, openOnCopy]);
  useEffect(() => {
    if (!running || !readId || typeof EventSource === "undefined") return;
    const stream = new EventSource("/v1/changes");
    let opened = false;
    stream.addEventListener("open", () => {
      // A reconnection: what was announced while the stream was down is lost.
      if (opened) void follow(readId, { unattended: true });
      opened = true;
    });
    stream.addEventListener("change", (ev) => {
      try { if (JSON.parse((ev as MessageEvent).data).kind !== "switch_read") return; } catch { /* read anyway */ }
      void follow(readId, { unattended: true });
    });
    return () => stream.close();
  }, [running, readId, follow]);
  // A read that ended with its document hands it on, once per record.
  const handed = useRef<string>(undefined);
  useEffect(() => {
    if (record?.state !== "done" || !record.reading || handed.current === record.id) return;
    handed.current = record.id;
    setReading(record.reading);
    onReading?.(record.reading, record.id);
  }, [record, onReading]);

  // A read the sheet asks for again, after a refusal: once per increment. It
  // reads the switch afresh, as *Read the switch again* does.
  const readRef = useRef<(fresh: boolean) => Promise<void>>(undefined);
  useEffect(() => { if (again > 0) void readRef.current?.(true); }, [again]);

  // fresh asks the server to read the switch even where it has a copy
  // (ADR-0560 decision 1c); without it, the copy answers with no session.
  const read = async (fresh: boolean) => {
    setReading(undefined); onReading?.(undefined); setRecord(undefined); handed.current = undefined;
    stopLane.clear();
    const r = await readLane.runWith(() => oneConsole(device.serial, "running-configuration:read", () => api.POST("/switches/{serial}/running-configuration:read", {
      params: { path: { serial: device.serial }, query: fresh ? { again: true } : {} },
    })));
    if (r.ok && r.data) {
      rememberRead(device.serial, r.data.id);
      setNow(Date.now());
      // A new id is read by the effect that follows it; the same id, a read
      // joined again, is read here.
      if (r.data.id === readId) void follow(r.data.id);
      else setReadId(r.data.id);
    }
  };
  useEffect(() => { readRef.current = read; });
  const stop = async () => {
    if (!record) return;
    const r = await stopLane.runWith(() => api.POST("/switches/{serial}/reads/{read_id}:stop", {
      params: { path: { serial: device.serial, read_id: record.id }, header: { "Idempotency-Key": idempotencyKey() } },
    }));
    if (r.ok && r.data) setRecord(r.data);
  };
  if (!mayRead) return null;

  const intro = (
    <>
      Reads the switch&rsquo;s own configuration over its console by typing a fixed list of <code>show</code> commands,
      and shows the answer as a configuration. Nothing is sent to the switch. The switch answers one command at a time,
      so this takes a while; the read goes on if you close this panel, and the panel shows which command it is on when
      you come back. {/* Restates ADR-0560 decisions 1c, 2b and 3a — the last read is kept as the switch's copy and used by
      every plan and apply of its configuration until its terminal is opened or Zenith writes to it, with no bound in time. */}
      The last read is kept, never anything the switch printed, and plans and applies on this switch use it instead of
      reading the switch again, until someone opens the switch&rsquo;s terminal or Zenith changes it. It is refused while
      a console or a PoE cycle is open on this switch.
    </>
  );
  const list = commands.data;
  const at = record?.step;
  const answered = record?.commands_answered ?? 0;
  const total = record?.commands_total || list?.commands.length || 0;
  const elapsed = record ? (record.finished_at ? Date.parse(record.finished_at) : now) - Date.parse(record.opened_at) : 0;
  const outcomeOf = (position: number): string => {
    if (record?.state === "done") return "answered";
    if (!at || position > at.position) return "not_typed";
    if (position < at.position) return "answered";
    return at.state === "typing" && !running ? "not_answered" : at.state;
  };
  const showOutcomes = record !== undefined && !running;
  const ended = record && (record.state === "failed" || record.state === "refused") ? record : undefined;
  const stoppedAt = ended && at && (at.state !== "answered" || answered < total) ? at : undefined;
  const endedProblem = ended ? describe(ended.problem, ended.status ?? 502) : undefined;
  const problem = readLane.problem ?? endedProblem;

  const counts = new Map<string, number>();
  for (const it of reading?.items ?? []) counts.set(it.disposition, (counts.get(it.disposition) ?? 0) + 1);
  const effects = new Map<Effect, number>();
  for (const it of reading?.items ?? []) effects.set(effectOf(it), (effects.get(effectOf(it)) ?? 0) + 1);
  const disagreeing = (reading?.comparison ?? []).filter((f) => !f.agrees);
  const readAt = reading ? new Date(reading.read_at) : undefined;

  return (
    <section className={embedded || step ? "config-read" : "panel"} aria-label="Running configuration">
      {/* Restates ADR-0115 decisions 3, 4 and 5, and ADR-0252 decision 5a — a closed list of show
          commands, one read per switch and none while a console or a PoE cycle is open on it, and
          nothing kept. Behind the ⓘ beside the heading since #1900, and still behind the disclosure
          on a step. */}
      {step
        ? <LearnMore>{intro}</LearnMore>
        : (
          <span className="page-title">
            {embedded ? <h3>Running configuration, read at the switch</h3> : <h2>Running configuration</h2>}
            <Info label="About this read">{intro}</Info>
          </span>
        )}
      {list && (list.admitted
        ? (
          // Restates ADR-0115 decision 3 — the list is the pair's own, closed and fixed, and every entry is a show command.
          <details>
            <summary>
              The {list.commands.length} <code>show</code> commands this read types on <code>{list.model}</code> on <code>{list.build}</code>
            </summary>
            <ol aria-label="Commands the read types">
              {list.commands.map((c, i) => (
                <li key={i}>
                  <code>{c}</code>
                  {showOutcomes && <> <span className="muted">— {wordFor(STEP_WORDS, outcomeOf(i + 1))}</span></>}
                </li>
              ))}
            </ol>
          </details>
        )
        : (
          // Restates ADR-0115 decision 2 — a model and build no capture admits is not read.
          <p className="muted">No capture admits <code>{list.model}</code> on <code>{list.build}</code>, so this switch is not read.</p>
        ))}
      <div className="actions">
        <button type="button" className="btn" onClick={() => void read(!!reading)} disabled={busy}>
          {busy ? "Reading the switch…" : reading ? "Read the switch again" : "Read the switch"}
        </button>
      </div>
      {running && record && (
        // Restates ADR-0115 decision 5 — progress names the command typed and whether it was answered, never what the switch printed.
        <div role="status" aria-label="Read progress">
          {at
            ? <p>Typing <code>{at.command}</code> · {at.position} of {at.total} · {clock(elapsed)}</p>
            : <p>Opening a console session on the switch · {clock(elapsed)}</p>}
          {total > 0 && <progress value={answered} max={total} aria-label={`${answered} of ${total} commands answered`} />}
          {/* Restates ADR-0530 decisions 1a and 5a — the read outlives this panel, and only its starter may stop it. */}
          <p className="muted">
            The read goes on if you close this panel.{record.yours ? "" : " Someone else started it, so only they can stop it."}
          </p>
          {record.yours && (
            <div className="actions">
              <button type="button" className="btn" onClick={() => void stop()} disabled={stopLane.busy || record.stop_requested}>
                {record.stop_requested ? "Stopping the read…" : "Stop the read"}
              </button>
            </div>
          )}
        </div>
      )}
      {stopLane.problem && <ProblemBox problem={stopLane.problem} onDismiss={stopLane.clear} />}
      {record?.state === "stopped" && <p>The read was stopped before it ended. Nothing was kept from it.</p>}
      {record?.state === "interrupted" && (
        <p>This read stopped without an answer: the service reading the switch restarted, or its time ran out. Read the switch again.</p>
      )}
      {stoppedAt && (
        <p>
          The read ended at command {stoppedAt.position} of {stoppedAt.total}, <code>{stoppedAt.command}</code>,{" "}
          {stoppedAt.state === "answered"
            ? "which the switch answered in a form the read does not take."
            : "which the switch did not answer."}{" "}
          {answered} of {total} commands were answered.
        </p>
      )}
      {problem && (
        <ProblemBox problem={problem} onDismiss={() => { readLane.clear(); if (ended) setRecord(undefined); }}
          head={problem.switch_read ? CANDIDATE_REFUSED : undefined} />
      )}
      {problem?.switch_session && (
        <HeldSwitch held={problem.switch_session} onRead={() => { readLane.clear(); void read(true); }} />
      )}
      {problem?.switch_read && (
        // Restates ADR-0115 decision 7a as ruled on #1788 — the candidate is refused whole, not the read's answer, so
        // what the read found is shown, and no configuration: nothing here can be approved or sent.
        <>
          <p>
            What the read found is below. No candidate configuration is shown, because it failed a check and nothing
            built from this read can be sent: the first-configuration apply reads the switch again and checks it again.
          </p>
          <dl className="facts">
            <dt>Commands typed</dt><dd>{problem.switch_read.commands}</dd>
            <dt>Secrets removed</dt><dd>{problem.switch_read.secrets}</dd>
          </dl>
          <Elements items={problem.switch_read.items} lead />
        </>
      )}

      {reading && readAt && (
        <>
          <dl className="facts">
            <dt>Read</dt>
            {/* Restates ADR-0115 decision 6 — the read is true of the moment it was taken and is never called current after it. */}
            <dd title="What the switch reported at this moment. It is not refreshed, and it is not called current after it.">{readAt.toLocaleString()}</dd>
            {record?.copy && (
              <>
                {/* Restates ADR-0560 decisions 1c, 2b and 6a and its Consequences — the switch's last read is what every plan
                    and apply is judged on until its terminal is opened or Zenith writes to it; a reboot, the controller's own
                    push or a console outside Zenith change the switch without replacing it. */}
                <dt>Used for</dt>
                <dd data-switch-copy>Plans and applies on this switch, until someone opens its terminal or Zenith changes it. A change made at the switch any other way is not seen until it is read again.</dd>
              </>
            )}
            <dt>Model and build</dt><dd><code>{reading.model}</code> on <code>{reading.build}</code></dd>
            <dt>Commands typed</dt><dd>{reading.commands}</dd>
            {/* Restates ADR-0115 decision 5 — secrets are counted, never shown. */}
            <dt>Secrets removed</dt><dd title="Lines naming a credential were taken out before anything read them. Only the count is kept.">{reading.secrets}</dd>
            <dt>Elements</dt>
            {reading.held === "document"
              ? (
                <dd>{(Object.keys(DISPOSITION_WORDS) as Disposition[]).filter((d) => counts.has(d)).map((d, i) => (
                  <span key={d}>{i > 0 && " · "}{counts.get(d)} {DISPOSITION_WORDS[d].label}</span>
                ))}{[...counts.keys()].filter((d) => !listed(DISPOSITION_WORDS, d)).map((d) => (
                  <span key={d}> · {counts.get(d)} {unrecognized(d)}</span>
                ))}</dd>
              )
              : (
                <dd>{(Object.keys(EFFECT_WORDS) as Effect[]).filter((e) => effects.has(e)).map((e, i) => (
                  <span key={e}>{i > 0 && " · "}{effects.get(e)} {EFFECT_WORDS[e][effects.get(e) === 1 ? 0 : 1]}</span>
                ))}</dd>
              )}
          </dl>

          {reading.held === "document" && (
            <>
              <h3>Against what the controller holds</h3>
              {disagreeing.length === 0
                ? <p>Every field the read models agrees with the configuration the controller holds.</p>
                : (
                  <div className="table-wrap"><table aria-label="Differences from the held configuration">
                    <thead><tr><th>Where</th><th>Field</th><th>On the switch</th><th>Held by the controller</th></tr></thead>
                    <tbody>{disagreeing.map((f) => {
                      const port = portOf(f.element);
                      return (
                        <tr key={f.element + f.field}>
                          <td>{port ? <PortName name={port} /> : <code>{f.element}</code>}</td>
                          <td>{f.field}</td><td>{f.read}</td><td>{f.held}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table></div>
                )}
              {/* Restates ADR-0115 decision 6 — a section the grammar does not model is shown as not read, never as agreeing. */}
              <p className="muted">Not read, so not compared and not counted as agreeing: {reading.not_read.join(", ")}.</p>
            </>
          )}
          {reading.held === "stub" && (
            // Restates ADR-0215 decisions 2c and 4c — for a switch whose cell holds only a stub the read is a candidate
            // first document, shown on one view, and it is sent only by an explicit apply on Design, whole and exactly as shown.
            <>
              {step
                ? (
                  <LearnMore summary="Candidate first configuration">
                    The controller holds no configuration for this switch, only a generation, so this is a candidate first configuration. It
                    has passed the schema, the parser and the floor. Reading it sends nothing. It can be sent as the switch&rsquo;s
                    first configuration, whole and exactly as shown, by one explicit apply on Design &rsaquo; Wired &rsaquo; Ports.
                    {/* Restates ADR-0555 decision 7a — where the switch's ports are planned, that apply sends them with it. */}
                    {" "}Where its ports are planned there, the same apply sends them with it.
                  </LearnMore>
                )
                : (
                  <p>
                    The controller holds no configuration for this switch, only a generation, so this is a candidate first configuration. It
                    has passed the schema, the parser and the floor. Reading it sends nothing. It can be sent as the switch&rsquo;s
                    first configuration, whole and exactly as shown, by one explicit apply on Design &rsaquo; Wired &rsaquo; Ports.
                    {/* Restates ADR-0555 decision 7a — where the switch's ports are planned, that apply sends them with it. */}
                    {" "}Where its ports are planned there, the same apply sends them with it.
                  </p>
                )}
              {!after && reading.first_document && reading.first_document.blockers.length > 0 && (
                <>
                  <p>It cannot be sent as things stand:</p>
                  <ul>{reading.first_document.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
                </>
              )}
            </>
          )}
          {reading.held === "none" && (
            // Restates ADR-0215 decision 4c — the first-document apply is for a switch whose cell holds a stub, and this one holds nothing.
            <p>
              The controller holds nothing for this switch, so this is a candidate configuration. It has passed the schema, the parser and
              the floor. This page does not send it: the first-configuration apply is for a switch whose controller holds a generation.
            </p>
          )}

          <Elements items={reading.items} lead={reading.held !== "document"} first={reading.first_document} model={reading.model} />

          {after
            ? after(reading)
            : reading.held === "document" && (
              <p className="muted">Keeping what was changed at the switch as its planned port settings is done on Design &rsaquo; Wired &rsaquo; Ports, where the switch can be read again and the result imported.</p>
            )}
        </>
      )}
    </section>
  );
}
